/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { GuardIcon } from '@atlaskit/logo';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type WithInteractionMode } from '../interaction-mode';

import messages from './messages';

type StoreGuardBlockViewCommonProps = {
	traceId?: string;
	blockType?: 'PROMPT_BLOCKED' | 'RESPONSE_BLOCKED' | 'ATTACHMENT_BLOCKED';
};

type StoreGuardBlockViewProps = WithInteractionMode<
	StoreGuardBlockViewCommonProps,
	StoreGuardBlockViewCommonProps
>;

const styles = cssMap({
	container: {
		paddingBlock: token('space.150'),
	},
	enforcementRow: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
	},
	bodyText: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
	},
	traceIdRow: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
});

/**
 * Renders the Guard block experience — shown when a user's prompt is blocked
 * by an Atlassian Guard data protection policy.
 */
const GuardBlockPresentation = ({
	traceId,
	blockType,
}: StoreGuardBlockViewCommonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const bodyMessage =
		blockType === 'RESPONSE_BLOCKED'
			? formatMessage(messages.blockedResponseBodyResponseBlocked)
			: formatMessage(messages.blockedResponseBody);

	return (
		<Box xcss={styles.container}>
			<Stack space="space.100">
				<Inline space="space.050" alignBlock="center" xcss={styles.enforcementRow}>
					<GuardIcon appearance="brand" shouldUseNewLogoDesign size="xsmall" />
					<Text>{formatMessage(messages.enforcementMessage)}</Text>
				</Inline>
				<Box xcss={styles.bodyText}>
					<Text>{bodyMessage}</Text>
				</Box>
				{traceId && (
					<Box xcss={styles.traceIdRow}>
						<Text>{formatMessage(messages.traceId, { traceId })}</Text>
					</Box>
				)}
			</Stack>
		</Box>
	);
};

export const StoreGuardBlockView = (props: StoreGuardBlockViewProps): React.JSX.Element | null => {
	if (props.interactionMode === 'read-only') {
		return null;
	}

	return <GuardBlockPresentation traceId={props.traceId} blockType={props.blockType} />;
};
